import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MailCheck } from "lucide-react";

export default function VerifyEmail() {
  const { verify, resendCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || "");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verify(email, code);
      toast.success("E-posta doğrulandı, hoş geldiniz!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (!email) return toast.error("Önce e-posta adresinizi girin");
    try {
      await resendCode(email);
      toast.success("Yeni kod e-postanıza gönderildi");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="verify-form">
        <MailCheck className="w-10 h-10 text-gold mb-5" />
        <p className="text-sm uppercase tracking-[0.3em] text-gold mb-3">Doğrulama</p>
        <h1 className="font-serif text-4xl mb-3">E-postanı doğrula</h1>
        <p className="text-white/60 mb-8 text-sm">
          <b className="text-white/80">{email || "e-posta adresinize"}</b> adresine gönderdiğimiz 6 haneli kodu girin.
        </p>
        <div className="space-y-5">
          {!location.state?.email && (
            <div>
              <Label className="text-white/70">E-posta</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                data-testid="verify-email-input"
                className="mt-2 bg-[#121212] border-white/10 focus:border-gold focus-visible:ring-gold h-11" />
            </div>
          )}
          <div>
            <Label className="text-white/70">Doğrulama Kodu</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required maxLength={6} inputMode="numeric"
              data-testid="verify-code-input"
              className="mt-2 bg-[#121212] border-white/10 focus:border-gold focus-visible:ring-gold h-14 text-center text-2xl tracking-[0.5em] font-mono"
              placeholder="______" />
          </div>
          <Button type="submit" disabled={loading || code.length !== 6} data-testid="verify-submit-button"
            className="w-full bg-gold text-black hover:bg-gold-light h-11 rounded-md">
            {loading ? "Doğrulanıyor…" : "Doğrula & Giriş Yap"}
          </Button>
        </div>
        <div className="flex items-center justify-between mt-6 text-sm">
          <button type="button" onClick={resend} data-testid="resend-code-button" className="text-gold hover:underline">
            Kodu tekrar gönder
          </button>
          <Link to="/login" className="text-white/50 hover:text-white">Girişe dön</Link>
        </div>
      </form>
    </div>
  );
}
